"use client";

import { useEffect, useState } from "react";
import { DownloadIcon, ShareIcon } from "@/components/icons";

/**
 * Mobile-only "install this PWA" nudge for the public landing page.
 *
 * OutRepR is already a fully installable PWA (manifest.ts → standalone),
 * but nothing surfaces the install option, so phone visitors never
 * discover the home-screen, full-screen experience. This renders a quiet,
 * tertiary affordance below the landing CTAs that adapts to the platform:
 *
 *   - Android / Chromium fire `beforeinstallprompt`. We capture it and
 *     offer a one-tap "Install app" button that calls `prompt()`.
 *   - iOS Safari / iOS Chrome NEVER fire that event — the only install
 *     path is the manual Share → "Add to Home Screen". So on iOS we show
 *     instructions, not a button.
 *
 * Hard constraints:
 *   - Targets phones + iPads, never desktop. The Android one-tap button
 *     only appears when we captured the install event — and we only
 *     capture on a mobile-width viewport (checked AT EVENT TIME), so a
 *     desktop browser keeps its own address-bar install icon and we never
 *     preventDefault there. The iOS branch is gated on iOS detection, NOT
 *     on viewport width, so an iPad (wider than a phone) still gets the
 *     Add-to-Home-Screen instruction.
 *   - Never show when already installed (running standalone).
 *   - Dismissible, persisted per-device like the shell hint.
 *
 * SSR-safe: starts `mounted=false`, so SSR + first client paint both
 * render null (no hydration flash); the mount effect then decides what,
 * if anything, to show. Mounted by the server landing page (page.tsx);
 * authed users are redirected away, so this only reaches logged-out
 * visitors. The landing stays dark — no light-mode handling here.
 */

/** The `beforeinstallprompt` event isn't in lib.dom — declare the slice
 *  we use. Kept file-local (no global WindowEventMap augmentation). */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

const STORAGE_KEY = "outrepr.installNudgeDismissed";

/** Mirror the `sm` breakpoint (640px) used everywhere else for
 *  "is this a phone-width viewport" — a phone is < 640 CSS px wide in
 *  portrait. Used only to gate the Android install-event capture (so we
 *  don't grab/preventDefault the event on desktop); iOS is gated on
 *  detection instead, so iPads are not excluded by width. */
const MOBILE_QUERY = "(max-width: 639px)";

function isIOSDevice(): boolean {
  const ua = navigator.userAgent;
  // iPhone/iPod/older iPad UA, plus iPadOS 13+ which reports as MacIntel
  // with a touch screen (no longer carries "iPad" in the UA). Macs have
  // no touchscreen (maxTouchPoints 0), so this never matches desktop.
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function InstallAppNudge() {
  const [mounted, setMounted] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [iosExpanded, setIosExpanded] = useState(false);

  useEffect(() => {
    // Already installed → never nudge. Bail before mounting so we render
    // null permanently for standalone launches.
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as { standalone?: boolean }).standalone === true;
    if (standalone) return;

    // Previously dismissed on this device?
    try {
      if (window.localStorage.getItem(STORAGE_KEY) === "1") {
        setDismissed(true);
      }
    } catch {
      // localStorage blocked (private mode) — just show as normal.
    }

    setIsIOS(isIOSDevice());

    // Android / Chromium: stash the install event. Gate on phone-width AT
    // EVENT TIME so a desktop browser keeps its native install affordance
    // (we must not preventDefault there). iOS never fires this.
    const onBeforeInstallPrompt = (e: Event) => {
      if (!window.matchMedia(MOBILE_QUERY).matches) return;
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);

    // Installed: Android/Chromium fires this for our button OR a
    // browser-menu install. Clear the captured event so the button
    // disappears. We deliberately do NOT persist a flag here — the
    // standalone check above already suppresses the nudge for installed
    // launches, and persisting would wrongly keep it hidden after a later
    // uninstall + revisit. (iOS fires neither this nor beforeinstallprompt;
    // the iOS re-prompt after a manual install is an unavoidable platform
    // gap, handled by the "Not now" dismiss.)
    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setDismissed(true);
    };
    window.addEventListener("appinstalled", onAppInstalled);

    setMounted(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  async function handleInstall() {
    const evt = deferredPrompt;
    if (!evt) return;
    await evt.prompt();
    await evt.userChoice;
    // The event is single-use — clear it so the button collapses. If the
    // user dismissed the native dialog they can reload to get it back.
    setDeferredPrompt(null);
  }

  function dismiss() {
    setDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore — nudge just won't persist its dismissal across reloads
    }
  }

  // Desktop renders nothing: deferredPrompt is only captured on a phone-width
  // viewport (gated at event time above), and isIOS is false on desktop — so
  // both branches fall through to `return null`. iOS (incl. iPad, which is
  // wider than a phone) is gated by isIOS, NOT by viewport width, so the
  // iPadOS case the detection targets actually renders.
  if (!mounted || dismissed) return null;

  // Shared pill styling — deliberately quieter + shorter (h-10) than the
  // 48px primary CTAs above, so it reads as a tertiary affordance.
  const pillClass =
    "inline-flex items-center gap-2 h-10 px-4 rounded-md bg-surface-2 border border-border text-fg-muted hover:text-fg hover:border-fg-dim text-sm font-medium transition-colors";
  const notNowClass =
    "text-fg-dim hover:text-fg text-xs transition-colors px-3 py-1.5 rounded-md";

  // Android / Chromium — one-tap install.
  if (deferredPrompt) {
    return (
      <div role="status" className="w-full flex flex-col items-center gap-2">
        <button type="button" onClick={handleInstall} className={pillClass}>
          <DownloadIcon className="w-4 h-4" />
          Install app
        </button>
        <button type="button" onClick={dismiss} className={notNowClass}>
          Not now
        </button>
      </div>
    );
  }

  // iOS — manual Share → Add to Home Screen (no install event exists).
  // Gated on isIOS only (not width) so iPads, which are wider than a
  // phone, still get the instruction.
  if (isIOS) {
    return (
      <div role="status" className="w-full flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={() => setIosExpanded((v) => !v)}
          aria-expanded={iosExpanded}
          aria-controls="install-ios-steps"
          className={pillClass}
        >
          <ShareIcon className="w-4 h-4" />
          Add to Home Screen
        </button>
        {iosExpanded && (
          <p
            id="install-ios-steps"
            className="text-fg-muted text-xs leading-relaxed max-w-[18rem] text-center"
          >
            Tap the{" "}
            <ShareIcon className="inline w-3.5 h-3.5 align-text-bottom text-brand" />{" "}
            <span className="text-fg font-medium">Share</span> button in your
            browser, then choose{" "}
            <span className="text-fg font-medium">Add to Home Screen</span>.
          </p>
        )}
        <button type="button" onClick={dismiss} className={notNowClass}>
          Not now
        </button>
      </div>
    );
  }

  // Mobile, but no actionable install path (e.g. Android Firefox, or the
  // event already fired/was consumed) — show nothing rather than a dead end.
  return null;
}
